import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Star, ShieldCheck, Calendar, Search, ArrowLeft, MapPin, Crown } from 'lucide-react';
import OptimizedImage from '../components/OptimizedImage';
import AutoTranslatedText from '../components/AutoTranslatedText';
import SEOHead from '../components/SEOHead';
import { generateBreadcrumbSchema, SEO_CONFIG } from '../utils/seo';
import { parseLocalDate } from '../utils/dateUtils';

const ExhibitorsPage = () => {
  const { t } = useTranslation();
  const [exhibitors, setExhibitors] = useState([]);
  const [filteredExhibitors, setFilteredExhibitors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterExhibitors();
  }, [exhibitors, searchTerm, selectedEvent, selectedTier]);

  const fetchData = async () => {
    try {
      const [exhibitorsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/all-exhibitors`),
        axios.get(`${API}/events`),
      ]);
      
      // Sort exhibitors by event start date, then alphabetically by company name
      const sorted = exhibitorsRes.data.sort((a, b) => {
        const dateA = a.event?.start_date ? parseLocalDate(a.event.start_date) : new Date(0);
        const dateB = b.event?.start_date ? parseLocalDate(b.event.start_date) : new Date(0);
        
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB; // Earlier dates first
        }
        return a.company_name.localeCompare(b.company_name);
      });
      
      setExhibitors(sorted);
      setFilteredExhibitors(sorted);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Failed to fetch exhibitors', error);
    } finally {
      setLoading(false);
    }
  };

  const filterExhibitors = () => {
    let filtered = [...exhibitors];
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(e => 
        e.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.seller?.company_info?.industry?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by event
    if (selectedEvent !== 'all') {
      filtered = filtered.filter(e => e.event_id === selectedEvent);
    }
    
    // Filter by tier
    if (selectedTier !== 'all') {
      filtered = filtered.filter(e => e.tier === selectedTier);
    }
    
    setFilteredExhibitors(filtered);
  };

  const getTierBadge = (tier) => {
    switch(tier) {
      case 'elite':
        return (
          <Badge className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0 shadow-md">
            <Crown className="h-3 w-3 mr-1 fill-yellow-300 text-yellow-300" />
            {t('exhibitors_page.elite').toUpperCase()}
          </Badge>
        );
      case 'premium':
        return (
          <Badge className="bg-orange-100 text-orange-700 border border-orange-200">
            <Star className="h-3 w-3 mr-1 fill-orange-500" />
            {t('exhibitors_page.premium').toUpperCase()}
          </Badge>
        );
      default:
        return <Badge className="bg-slate-100 text-slate-600 border border-slate-200">{t('exhibitors_page.basic').toUpperCase()}</Badge>;
    }
  };

  const getEventStatusBadge = (status) => {
    switch(status) {
      case 'live':
        return (
          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded flex items-center">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1 animate-pulse"></span>
            {t('exhibitors_page.status_live')}
          </span>
        );
      case 'upcoming':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">{t('exhibitors_page.status_upcoming')}</span>;
      case 'registration_open':
        return <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">{t('exhibitors_page.status_registration_open')}</span>;
      case 'completed':
        return <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">{t('exhibitors_page.status_past')}</span>;
      default:
        return <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">{status?.toUpperCase()}</span>;
    }
  };

  // Breadcrumb schema for search results
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Exhibitors' }
  ]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Featured Exhibitors | TradeHub24 Virtual Tradeshow"
        description="Explore booths from verified exhibitors. View products, request demos, and connect with suppliers from around the world."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/exhibitors`}
        structuredData={breadcrumbSchema}
      />
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-1" /> {t('exhibitors_page.back')}
              </Button>
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('exhibitors_page.title')}</h1>
          <p className="text-slate-600">{t('exhibitors_page.subtitle')}</p>
          <p className="text-sm text-slate-500 mt-1">
            <Star className="inline h-4 w-4 text-amber-500 fill-amber-500 mr-1" />
            {t('exhibitors_page.featured_note')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('exhibitors_page.search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                <SelectTrigger className="w-full sm:w-64">
                  <SelectValue placeholder={t('exhibitors_page.filter_by_exhibit')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('exhibitors_page.all_exhibits')}</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.name} ({event.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Tier Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-slate-500 mr-2 self-center">{t('exhibitors_page.filter_by_tier')}</span>
              {['all', 'elite', 'premium', 'basic'].map((tier) => (
                <Button
                  key={tier}
                  variant={selectedTier === tier ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedTier(tier)}
                  className={tier === 'elite' && selectedTier === tier ? 'bg-purple-600 hover:bg-purple-700' : 
                            tier === 'premium' && selectedTier === tier ? 'bg-orange-500 hover:bg-orange-600' :
                            tier === 'basic' && selectedTier === tier ? 'bg-slate-500 hover:bg-slate-600' : ''}
                >
                  {t(`exhibitors_page.${tier}`)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Exhibitors List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredExhibitors.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">{t('exhibitors_page.no_exhibitors')}</h3>
            <p className="text-slate-500">{t('exhibitors_page.no_exhibitors_hint')}</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4">
              {filteredExhibitors.length === 1 
                ? t('exhibitors_page.showing_exhibitors', { count: filteredExhibitors.length })
                : t('exhibitors_page.showing_exhibitors_plural', { count: filteredExhibitors.length })}
              {selectedEvent !== 'all' && ` ${t('exhibitors_page.for_selected_event')}`}
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredExhibitors.map((booth) => (
                <Link
                  key={booth.id}
                  to={user ? `/booth/${booth.id}` : '/auth'}
                  className="group"
                >
                  <div className={`bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-all h-full flex flex-col ${
                    booth.is_featured 
                      ? 'border-amber-300 ring-2 ring-amber-100' 
                      : 'border-slate-200'
                  }`}>
                    {/* Header with Featured Badge */}
                    {booth.is_featured && (
                      <div className="bg-amber-50 px-4 py-2 border-b border-amber-200">
                        <span className="inline-flex items-center text-xs font-medium text-amber-700">
                          <Star className="h-3 w-3 mr-1 fill-amber-500" />
                          {t('exhibitors_page.featured_exhibitor')}
                        </span>
                      </div>
                    )}
                    
                    {/* Logo/Banner Area */}
                    <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center relative overflow-hidden">
                      {booth.banner_url ? (
                        <OptimizedImage 
                          src={booth.banner_url} 
                          alt={booth.company_name} 
                          className="w-full h-full" 
                          objectFit="cover"
                        />
                      ) : booth.logo_url ? (
                        <OptimizedImage 
                          src={booth.logo_url} 
                          alt={booth.company_name} 
                          className="max-w-full max-h-full p-4"
                          objectFit="contain"
                        />
                      ) : (
                        <Building2 className="h-12 w-12 text-slate-300" />
                      )}
                      
                      {/* Tier Badge */}
                      <div className="absolute top-2 right-2">
                        {getTierBadge(booth.tier)}
                      </div>
                      
                      {/* Verified Badge */}
                      {booth.seller?.is_verified && (
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-green-100 text-green-700 border border-green-200">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            {t('exhibitors_page.verified')}
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-primary transition-colors">
                        {booth.company_name}
                      </h3>
                      
                      {booth.seller?.company_info?.industry && (
                        <p className="text-xs text-slate-500 mb-2">{booth.seller.company_info.industry}</p>
                      )}
                      
                      {booth.slogan && (
                        <p className="text-sm text-slate-600 italic line-clamp-1 mb-2">
                          &ldquo;<AutoTranslatedText text={booth.slogan} />&rdquo;
                        </p>
                      )}
                      
                      {booth.description && (
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3 flex-1">
                          <AutoTranslatedText text={booth.description} />
                        </p>
                      )}
                      
                      {/* Location */}
                      {booth.seller?.company_info?.city && (
                        <div className="text-xs text-slate-500 mb-2 flex items-center">
                          <MapPin className="h-3 w-3 mr-1" />
                          {booth.seller.company_info.city}, {booth.seller.company_info.country}
                        </div>
                      )}
                      
                      {/* Event Info */}
                      {booth.event && (
                        <div className="mt-auto pt-3 border-t border-slate-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-xs text-slate-500">
                              <Calendar className="h-3 w-3 mr-1" />
                              <span className="truncate">{booth.event.name}</span>
                            </div>
                            {getEventStatusBadge(booth.event.status)}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {parseLocalDate(booth.event.start_date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      {/* Login prompt for non-logged users */}
                      {!user && (
                        <div className="mt-3 pt-2 border-t border-slate-100">
                          <p className="text-xs text-primary font-medium">{t('exhibitors_page.sign_in_to_view')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExhibitorsPage;
